import ENDPOINTS from "./Endpoints";
import ApiMethods from "./ApiMethods";

/**
 * StorageManager - A class for handling cloud storage operations
 * This class provides methods for uploading, downloading, and managing files in cloud storage
 * It uses the new Cloud Storage API which is storage-agnostic and can work with different providers
 */
class StorageManager {
    /**
     * Upload a file to cloud storage
     * @param {File} file - The file to upload
     * @param {Object} options - Upload options
     * @param {string} options.directory - Optional directory to store the file in (default: 'uploads')
     * @returns {Promise<Response>} - The response from the API
     */
    static uploadFile = (file, options = {}) => {
        const { directory = 'uploads' } = options;
        
        let url = ENDPOINTS.STORAGE_UPLOAD;
        const queryParams = new URLSearchParams();
        
        if (directory) {
            queryParams.append('directory', directory);
        }
        
        const queryString = queryParams.toString();
        if (queryString) {
            url = `${url}?${queryString}`;
        }
        
        const formData = new FormData();
        formData.append("file", file);
        
        const headers = {
            Authorization: ApiMethods.getAuthHeader(),
        };
        
        const requestOptions = {
            method: "POST",
            headers,
            body: formData,
        };
        
        return ApiMethods.fetchWithAuth(url, requestOptions);
    };
    
    /**
     * Download a file from cloud storage
     * @param {string} fileName - The name of the file to download
     * @returns {Promise<Response>} - The response from the API
     */
    static downloadFile = (fileName) => {
        let url = ENDPOINTS.STORAGE_DOWNLOAD;
        const queryParams = new URLSearchParams();
        queryParams.append('file_name', fileName);
        
        const queryString = queryParams.toString();
        if (queryString) {
            url = `${url}?${queryString}`;
        }
        
        return ApiMethods.get(url);
    };
    
    /**
     * Delete a file from cloud storage
     * @param {string} fileName - The name of the file to delete
     * @returns {Promise<Response>} - The response from the API
     */
    static deleteFile = (fileName) => {
        let url = ENDPOINTS.STORAGE_DELETE;
        const queryParams = new URLSearchParams();
        queryParams.append('file_name', fileName);
        
        const queryString = queryParams.toString();
        if (queryString) {
            url = `${url}?${queryString}`;
        }
        
        return ApiMethods.delete(url);
    };
    
    /**
     * Upload raw data as a file to cloud storage
     * @param {any} data - The data to upload
     * @param {Object} options - Upload options
     * @param {string} options.filename - Required filename for the uploaded data
     * @param {string} options.directory - Optional directory to store the file in (default: 'uploads')
     * @param {string} options.contentType - Optional content type (default: determined by the server)
     * @returns {Promise<Response>} - The response from the API
     */
    static uploadBytes = (data, options = {}) => {
        const { filename, directory = 'uploads', contentType } = options;
        
        if (!filename) {
            throw new Error('Filename is required for uploadBytes');
        }
        
        let url = ENDPOINTS.STORAGE_UPLOAD_BYTES;
        const queryParams = new URLSearchParams();
        queryParams.append('filename', filename);
        
        if (directory) {
            queryParams.append('directory', directory);
        }
        
        const queryString = queryParams.toString();
        if (queryString) {
            url = `${url}?${queryString}`;
        }
        
        const headers = {
            Authorization: ApiMethods.getAuthHeader(),
        };
        
        if (contentType) {
            headers['Content-Type'] = contentType;
        }
        
        const requestOptions = {
            method: "POST",
            headers,
            body: data,
        };
        
        return ApiMethods.fetchWithAuth(url, requestOptions);
    };
    
    /**
     * Generate a presigned URL for direct file upload
     * @param {Object} options - Options for the presigned URL
     * @param {string} options.filename - Required filename for the upload
     * @param {string} options.directory - Optional directory to store the file in (default: 'uploads')
     * @param {number} options.expiration - Optional expiration time in minutes (default: 15, max: 1440)
     * @returns {Promise<Response>} - The response from the API
     */
    static getPresignedUploadUrl = (options = {}) => {
        const { filename, directory = 'uploads', expiration } = options;
        
        if (!filename) {
            throw new Error('fileName is required for presigned upload URL');
        }
        
        let url = ENDPOINTS.STORAGE_PRESIGNED_UPLOAD;
        const queryParams = new URLSearchParams();
        queryParams.append('fileName', filename);
        
        if (directory) {
            queryParams.append('directory', directory);
        }
        
        if (expiration) {
            queryParams.append('expiration', expiration.toString());
        }
        
        const queryString = queryParams.toString();
        if (queryString) {
            url = `${url}?${queryString}`;
        }
        
        return ApiMethods.get(url);
    };
    
    /**
     * Generate a presigned URL for direct file download
     * @param {string} fileName - The name of the file to download
     * @param {Object} options - Options for the presigned URL
     * @param {number} options.expiration - Optional expiration time in minutes (default: 15, max: 1440)
     * @returns {Promise<Response>} - The response from the API
     */
    static getPresignedDownloadUrl = (fileName, options = {}) => {
        const { expiration } = options;
        
        let url = ENDPOINTS.STORAGE_PRESIGNED_DOWNLOAD;
        const queryParams = new URLSearchParams();
        queryParams.append('fileName', fileName);
        
        if (expiration) {
            queryParams.append('expiration', expiration.toString());
        }
        
        const queryString = queryParams.toString();
        if (queryString) {
            url = `${url}?${queryString}`;
        }
        
        return ApiMethods.get(url);
    };
    
    /**
     * Save metadata for a file that was uploaded via presigned URL
     * @param {string} fileName - The name of the file
     * @returns {Promise<Response>} - The response from the API
     */
    static saveMetadata = (fileName, fileKey) => {
        let url = ENDPOINTS.STORAGE_SAVE_METADATA;
        const queryParams = new URLSearchParams();
        queryParams.append('fileName', fileName);
        queryParams.append('fileKey', fileKey);
        
        const queryString = queryParams.toString();
        if (queryString) {
            url = `${url}?${queryString}`;
        }
        
        return ApiMethods.post(url);
    };
    
}

export default StorageManager;
